import type { Zone } from "@/types/fleet";
import { apiRequest } from "@/lib/api/http";
import { logCrud } from "@/lib/activity/emit-store";

interface RemoteZone {
  id: string;
  title: string;
  type: "parking" | "nogo" | "go";
  description?: string;
  coordinates: string | [number, number][];
}

interface ListResponse<T> {
  success: true;
  data: T[];
}

function parseCoordinates(input: string | [number, number][]): [number, number][] {
  if (Array.isArray(input)) {
    return input;
  }

  try {
    const parsed = JSON.parse(input) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => Array.isArray(item) && item.length >= 2)
      .map((item) => [Number(item[0]), Number(item[1])] as [number, number])
      .filter((item) => Number.isFinite(item[0]) && Number.isFinite(item[1]));
  } catch {
    return [];
  }
}

function fromRemoteType(type: RemoteZone["type"]): Zone["type"] {
  if (type === "nogo") return "no_go";
  if (type === "go") return "driving";
  return "parking";
}

function toRemoteType(type: Zone["type"]): RemoteZone["type"] {
  if (type === "no_go") return "nogo";
  if (type === "driving" || type === "custom") return "go";
  return "parking";
}

function colorByType(type: Zone["type"]) {
  if (type === "no_go") return "#ef4444";
  if (type === "driving") return "#22c55e";
  if (type === "custom") return "#8b5cf6";
  return "#3b82f6";
}

function mapZone(zone: RemoteZone): Zone {
  const type = fromRemoteType(zone.type);
  return {
    id: zone.id,
    name: zone.title,
    type,
    color: colorByType(type),
    coordinates: parseCoordinates(zone.coordinates),
    description: zone.description ?? "",
    createdAt: new Date().toISOString(),
  };
}

export async function listZones(): Promise<Zone[]> {
  const response = await apiRequest<ListResponse<RemoteZone>>("/api/zones", {
    method: "GET",
    cache: "no-store",
  });
  return response.data.map(mapZone);
}

export async function createZone(input: {
  name: string;
  type: Zone["type"];
  coordinates: [number, number][];
  description?: string;
}): Promise<void> {
  const response = await apiRequest<{ success: true; id: string }>("/api/zones", {
    method: "POST",
    body: JSON.stringify({
      title: input.name,
      type: toRemoteType(input.type),
      description: input.description ?? "",
      coordinates: JSON.stringify(input.coordinates),
    }),
  });
  logCrud("create", "zone", input.name, response.id);
}

export async function updateZone(
  id: string,
  input: {
    name: string;
    type: Zone["type"];
    description?: string;
    coordinates?: [number, number][];
  },
): Promise<void> {
  // Upstream PUT treats every field as optional, so only send what the caller supplied.
  const body: Record<string, string> = {
    title: input.name,
    type: toRemoteType(input.type),
  };
  if (input.description !== undefined) {
    body.description = input.description;
  }
  if (input.coordinates !== undefined) {
    body.coordinates = JSON.stringify(input.coordinates);
  }

  await apiRequest<{ success: true }>(`/api/zones/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
  logCrud("update", "zone", input.name, id);
}

export async function deleteZone(id: string): Promise<void> {
  await apiRequest<{ success: true }>(`/api/zones/${id}`, {
    method: "DELETE",
  });
  logCrud("delete", "zone", "", id);
}

